// frontend/src/pages/auth/ForgotPassword.js
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import mayfairLogo from '../../logo.svg';
import api from '../../services/api';

export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [emailFocused, setEmailFocused] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate email
        if (!email) {
            setError('Please enter your email address.');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address.');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess(false);

        try {
            const { data } = await api.post('/auth/forgot-password', { email });
            console.log('[ForgotPassword] Reset link sent:', data);
            setSuccess(true);
            setEmail('');
        } catch (err) {
            console.error('[ForgotPassword] Error:', err);
            setError(err.response?.data?.error || 'Unable to send reset link. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#2d5232] to-[#3a663f] shadow-2xl shadow-[#3a663f]/25 p-3">
                        <img
                            src={mayfairLogo}
                            alt="Mayfair Insurance"
                            className="w-full h-full object-contain"
                        />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mt-4">Reset Password</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Enter your email address and we'll send you a link to reset your password.
                    </p>
                </div>

                {/* Form Card */}
                <div className="bg-white rounded-3xl shadow-2xl border border-gray-100/80 p-8 md:p-10">
                    {success ? (
                        // Success State
                        <div className="text-center space-y-4 animate-fadeIn">
                            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100">
                                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-800">Check Your Email</h3>
                                <p className="text-sm text-gray-500 mt-2">
                                    We've sent a password reset link to your email address.
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                    Please check your inbox and follow the instructions.
                                </p>
                            </div>
                            <div className="pt-4 space-y-3">
                                <button
                                    onClick={() => setSuccess(false)}
                                    className="w-full text-sm text-[#3a663f] hover:text-[#2d5232] font-medium transition-colors"
                                >
                                    Didn't receive the email? Try again
                                </button>
                                <Link
                                    to="/login"
                                    className="block w-full bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-semibold py-3 rounded-2xl shadow-lg shadow-[#3a663f]/20 hover:shadow-xl transition-all duration-300 text-center"
                                >
                                    Back to Login
                                </Link>
                            </div>
                        </div>
                    ) : (
                        // Form State
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Error Alert */}
                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl flex items-start gap-3 animate-fadeIn shadow-sm">
                                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                                        <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </div>
                                    <span className="text-sm flex-1">{error}</span>
                                    <button
                                        type="button"
                                        onClick={() => setError('')}
                                        className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            )}

                            {/* Email Field */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Email Address
                                </label>
                                <div className="relative">
                                    <div className={`absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center transition-colors duration-200 ${emailFocused || email ? 'text-[#3a663f]' : 'text-gray-400'
                                        }`}>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (error) setError('');
                                        }}
                                        onFocus={() => setEmailFocused(true)}
                                        onBlur={() => setEmailFocused(false)}
                                        placeholder="you@example.com"
                                        className={`w-full pl-12 pr-4 py-3.5 border-2 rounded-2xl focus:ring-4 focus:ring-[#3a663f]/10 focus:border-[#3a663f] outline-none text-gray-700 placeholder-gray-400 transition-all duration-200 ${error ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                            }`}
                                        required
                                        autoComplete="email"
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={loading}
                                className="relative w-full group overflow-hidden bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-semibold py-3.5 rounded-2xl shadow-lg shadow-[#3a663f]/20 hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
                            >
                                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>
                                <span className="relative flex items-center justify-center gap-3 text-lg">
                                    {loading ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                            Send Reset Link
                                            <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </>
                                    )}
                                </span>
                            </button>

                            {/* Back to Login */}
                            <Link
                                to="/login"
                                className="block text-center text-sm text-[#3a663f] hover:text-[#2d5232] font-medium transition-colors"
                            >
                                ← Back to Login
                            </Link>
                        </form>
                    )}
                </div>

                {/* Footer */}
                <div className="text-center text-xs text-gray-400 mt-6 space-y-1">
                    <p>© 2026 Mayfair Insurance Company Limited. All rights reserved.</p>
                    <p className="flex items-center justify-center gap-2">
                        <span>🔒 Secure</span>
                        <span className="w-px h-3 bg-gray-300"></span>
                        <span>🛡️ Protected</span>
                    </p>
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-in-out;
                }
            `}</style>
        </div>
    );
}